import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import type { DocumentRecord } from "@shared/types";

// Reuses DocumentsTable's existing status-chip palette (status-draft's
// amber "needs attention" look fits pending_review; status-approved is
// shared as-is) rather than inventing a parallel set of colors — only
// "rejected" needed a genuinely new one.
const REVIEW_STATUS_CHIP: Record<DraftTable["reviewStatus"], string> = {
  pending_review: "status-draft",
  approved: "status-approved",
  rejected: "status-rejected",
};
const REVIEW_STATUS_LABEL: Record<DraftTable["reviewStatus"], string> = {
  pending_review: "pending review",
  approved: "approved",
  rejected: "rejected",
};

interface DraftTableRow {
  id: string;
  rowIndex: number;
  fields: Record<string, string>;
}

interface DraftTable {
  id: string;
  page: number;
  kindLabel: string | null;
  headers: string[];
  reviewStatus: "pending_review" | "approved" | "rejected";
  agentConfidence: "high" | "low" | null;
  agentNotes: string | null;
  rows: DraftTableRow[];
}

interface DraftTableReviewModalProps {
  projectId: string;
  document: DocumentRecord;
  onClose: () => void;
  /** Called after an approve/reject action succeeds, so the caller can refresh its own document list (pendingTableReviewCount changes). */
  onChanged: () => void;
}

// Agent-drafted tables (extraction_source='agent_drafted' — see
// db/migrations/063_agent_drafted_table_review.sql) become citable in
// general Q&A search once approved here. Approving does NOT promote a
// table into structured entity/attribute data (deterministic attribute
// lookups) — that stays the same hand-verified manual-migration process
// used for every one of the app's other table kinds. See this session's
// own plan doc for why that scope split was chosen for v1.
export function DraftTableReviewModal({ projectId, document, onClose, onChanged }: DraftTableReviewModalProps) {
  const [tables, setTables] = useState<DraftTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    setLoading(true);
    api
      .get<{ tables: DraftTable[] }>(`/api/projects/${projectId}/documents/${document.id}/draft-tables`)
      .then((data) => setTables(data.tables))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load draft tables."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [projectId, document.id]);

  async function act(table: DraftTable, action: "approve" | "reject") {
    setBusyId(table.id);
    try {
      await api.post(`/api/projects/${projectId}/documents/${document.id}/draft-tables/${table.id}/${action}`);
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action === "approve" ? "Approval" : "Rejection"} failed.`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal draft-table-review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Draft tables — {document.title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          These tables were read by an AI extraction pass because their layout didn't match any table shape this app
          already recognizes. Review each one before approving — approving makes it findable in chat answers; it does
          not add it to structured lookups.
        </p>
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : tables.length === 0 ? (
          <p className="muted">No draft tables for this document.</p>
        ) : (
          tables.map((table) => (
            <div key={table.id} className="draft-table-card">
              <div className="draft-table-card-head">
                <div>
                  <strong>{table.kindLabel ?? "Unlabeled table"}</strong> — page {table.page}
                  {table.agentConfidence === "low" && <span className="status-chip status-draft"> low confidence</span>}
                  <span className={`status-chip ${REVIEW_STATUS_CHIP[table.reviewStatus]}`}> {REVIEW_STATUS_LABEL[table.reviewStatus]}</span>
                </div>
                <div>
                  <button
                    className="btn"
                    disabled={busyId === table.id || table.reviewStatus === "approved"}
                    onClick={() => act(table, "approve")}
                  >
                    {busyId === table.id ? "Approving…" : "Approve"}
                  </button>{" "}
                  <button
                    className="btn btn-danger"
                    disabled={busyId === table.id || table.reviewStatus === "rejected"}
                    onClick={() => act(table, "reject")}
                  >
                    {busyId === table.id ? "Rejecting…" : "Reject"}
                  </button>
                </div>
              </div>
              {table.agentNotes && <p className="draft-table-notes">{table.agentNotes}</p>}
              <div className="table-wrap">
                <table className="draft-table-preview">
                  <thead>
                    <tr>
                      {table.headers.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row) => (
                      <tr key={row.id}>
                        {table.headers.map((h) => (
                          <td key={h}>{row.fields[h] ?? ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
